import type { StorefrontMetafield } from '@/features/storefront/api/storefront';
import type { ContentReader } from '@/features/storefront/content/useContent';
import { formatDate, getActiveLocale } from '@/lib/i18n/format';

/**
 * Reading a merchant's structured facts about a product.
 *
 * Apart from the components that draw them, because two themes now need these
 * rules and only one of them uses the shared spec table: Hyper folds the same
 * metafields into its own accordion and its own comparison columns, and a copy
 * of "what counts as prose" living in a theme folder is a copy that will
 * disagree with this one the first time the rule changes.
 */

/**
 * A stored date read the way a shopper writes one, and untouched if whatever
 * was stored is not a date at all.
 */
function formatMetaDate(value: unknown): string {
  const raw = String(value ?? '');
  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime())
    ? raw
    : formatDate(parsed, getActiveLocale(), { day: 'numeric', month: 'short', year: 'numeric' });
}

/**
 * A scalar metafield as text; a list becomes a comma-separated one. A yes/no
 * field reads in the storefront's language when the shop's copy is passed.
 */
export function formatMetafieldValue(value: unknown, fieldType = '', copy?: ContentReader): string {
  if (Array.isArray(value)) {
    return value
      .map((item) => formatMetafieldValue(item, fieldType, copy))
      .filter(Boolean)
      .join(', ');
  }
  if (typeof value === 'boolean') {
    if (copy) return copy(value ? 'common.yes' : 'common.no');
    return value ? 'Yes' : 'No';
  }
  if (value === null || value === undefined) return '';
  if (fieldType === 'date') return formatMetaDate(value);
  return String(value);
}

/**
 * The lines of a multi-line field. Merchants type these as a list, with or
 * without a bullet in front of each one, so the bullet the page draws is not
 * doubled by the one they typed.
 */
export function metaTextLines(value: unknown): string[] {
  const raw = Array.isArray(value) ? value : [value];
  return raw
    .flatMap((item) => String(item ?? '').split(/\r?\n/))
    .map((line) => line.trim().replace(/^[-•*]\s+/, ''))
    .filter(Boolean);
}

/** A metafield with neither entries nor a value has no row worth drawing. */
export function hasMetaContent(metafield: StorefrontMetafield): boolean {
  if (metafield.entries.length > 0) return true;
  const { value } = metafield;
  if (Array.isArray(value)) return value.length > 0;
  return value !== null && value !== undefined && String(value).trim() !== '';
}

/**
 * Free-typed prose - highlights, a care story - reads as a block of its own.
 * A two-column row is for a fact short enough to sit beside its name.
 */
export function isMetaProse(metafield: StorefrontMetafield): boolean {
  return metafield.field_type === 'multi_line_text' && metafield.entries.length === 0;
}
